<template>
  <!------------------------------------------
    This component displays the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-district-print-v">
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <Block v-else>
      <div class="print-instruction no-print">
        Use your browser's print functionality to print the page view below. Depending on what you see in the print preview, you may need to change the settings to enable "background graphics" (blue shading), disable "headers and footers" (the print date and other page info), and adjust margins. The layout has been developed and tested for printing from Google Chrome; results may vary on other browsers.
      </div>
      <div id="cd-summary-area-print" v-if="profileData !== undefined && profileData !== null">
        <!-- HEADER AREA -->
        <div class="grid-header">
          <div class="grid-header-left">
            <div class="main-title">
              <div class="title-1">Civil Legal needs & services in</div>
              <div class="title-2"><h2>{{ profileData.state }}'s {{ selectedCDLabel }}</h2></div>
            </div>
          </div>
          <div class="grid-header-right">
            <div class="grid-lsc-and-qr-info">
              <div class="summary-logo">
                <div><img id="summary-logo-img" :src="require(`@/assets/images/logo/lsc-logo-large.jpg`)"></div>
              </div>
              <div class="qr-info">
                Web version & more info
                <v-icon size="22" id="qr-right-arrow">
                  {{ icons.rightArrow }}
                </v-icon>
              </div>
            </div>
            <QrCode :url="qrUrl" :key="qrUrl" />
          </div>
        </div>

        <!-- JUSTICE GAP & ELIGIBILITY -->
        <div class="grid-gap grid-section custom-grid-section shaded grid-first-row">
          <div class="section-title">
            <span class="section-title-emphasis">THE JUSTICE GAP</span> <br><span class="section-subtitle">facing constituents</span>
          </div>
          <div id="justice-gap-points">
            <div class="justice-gap-row">
              <div class="justice-gap-data-point">{{ percentToOneInX(profileData.population.pct_eligible) }}</div>
              <div class="justice-gap-data-text">constituents in the district ({{ formatThousandsNoDecimal(profileData.population.eligible) }}) are eligible for LSC services.</div>
            </div>
            <div class="justice-gap-row">
              <div class="justice-gap-data-point">3 in 4</div>
              <div class="justice-gap-data-text">will face at least one civil legal problem this year.</div>
            </div>
            <div class="justice-gap-row">
              <div class="justice-gap-data-point">Only {{ formatPercentNoDecimalIfExists(profileData.justice_gap.problems_pct_unhelped) }}</div>
              <div class="justice-gap-data-text">of their legal problems will receive enough legal help.</div>
            </div>
          </div>
          <div id="justice-gap-icon-design">
            <div id="ppl-icon" class="icon-div">
              <v-icon color="#5a5a5a" size="25">
                  {{ icons.people }}
              </v-icon>
            </div>
            <div id="dotted-path"></div>
            <div id="justice-icon" class="icon-div">
              <v-icon color="#5a5a5a" size="25">
                  {{ icons.scale }}
              </v-icon>
            </div>
          </div>
          <div id="justice-gap-eligible">
            <div class="jg-eligible-row">
              <div id="eligible-header">ALSO ELIGIBLE</div>
              <ProfileDataPoint id="eligible-seniors" class="print-v-medium" icon="seniors" :dataPoint="percentToOneInX(profileData.population.seniors_eligible)" text="older Americans" :sideBySide="true"/>
            </div>
            <div class="jg-eligible-row">
              <ProfileDataPoint class="print-v-medium" icon="child" :dataPoint="percentToOneInX(profileData.population.pct_children_in_elig_hh)" text="children" :sideBySide="true"/>
              <ProfileDataPoint class="print-v-medium" icon="star" :dataPoint="percentToOneInX(profileData.population.vet_pov_pct)" text="veterans" :sideBySide="true"/>
            </div>
          </div>
        </div>

        <!-- SERVED & HOUSING STATS -->
        <div class="grid-housing grid-section custom-grid-section grid-first-row">
          <div class="section-title">
            <span class="section-title-emphasis">Constituents Served</span><br><span class="section-subtitle">in {{ profileData.gar_year }}</span>
          </div>
          <div id="served-in-district">
            <ProfileDataPoint class="print-v-medium" :dataPoint="formatThousandsNoDecimal(profileData.served.people)" icon="people" text="people" :sideBySide="true" iconColor="#ccc"/>
            <ProfileDataPoint class="print-v-medium" :dataPoint="formatThousandsNoDecimal(profileData.served.dv)" text="DV survivors" icon="dv" :sideBySide="true" iconColor="#ccc"/>
            <ProfileDataPoint class="print-v-medium" :dataPoint="formatThousandsNoDecimal(profileData.served.veterans)" icon="star" text="veterans" :sideBySide="true" iconColor="#ccc"/>
            <ProfileDataPoint  class="print-v-medium" :dataPoint="formatThousandsNoDecimal(profileData.served.seniors)" icon="seniors" text="older Americans" :sideBySide="true" iconColor="#ccc"/>
          </div>
          <div class="section-title">
            <span class="section-title-emphasis">Housing Issues</span> <br><span class="section-subtitle">facing constituents</span>
          </div>
          <div id="housing-data">
            <ProfileDataPoint class="print-v-medium" v-for="pnt in housingPointsPrint" :key="pnt.name" :introText="pnt.introText" :sideBySide="pnt.sideBySide" :dataPoint="String(pnt.dataPoint)" :text="pnt.text" :comparisonDataPoint="pnt.comparisonDataPoint" :comparisonText="pnt.comparisonText" :afterText="pnt.afterText" :inlinePoint="pnt.inlinePoint" />
          </div>
        </div>

        <!-- GRANTEES LIST -->
        <div class="grid-grantees grid-section custom-grid-section grid-second-row">
          <div class="section-title" :class="'grantee-count-' + String(numGrantees)">
            <span class="section-title-emphasis">LSC Grantees</span>
          </div>
          <div id="grantees-list">
            <div class="grantees-list-g" :class="'grantee-count-' + String(numGrantees)" v-for="g in granteeDataPoints" :key="g.rno">
              <div class="grantee-name-row">
                <v-icon v-for="area in colorPerServArea.filter(x => x.rno === g.rno)"
                  :key="area.color"
                  :color="area.color"
                  class="grantee-color"
                  size="22">
                    {{ icons.circle }}
                  </v-icon>
                <div class="grantee-name">{{ g.data.org_name }}</div>
              </div>
              <div class="grantee-serves grantees-list-info">
                <v-icon
                  color="#aaa"
                  class="grantees-list-icon"
                  size="17">
                    {{ icons.serves }}
                </v-icon>
                <div><span class="grantee-info-subheader">Serves: </span>{{ formatPercentNoDecimalIfExists(g.data.percent_of_constituents) }} of eligible constituents</div>
              </div>
              <div v-if="numGrantees <= 3" class="grantee-cases grantees-list-info">
                <v-icon
                  color="#aaa"
                  class="grantees-list-icon"
                  size="17">
                    {{ icons.gavel }}
                </v-icon>
                <div><span class="grantee-info-subheader">Top cases: </span>{{ g.cases }}</div>
              </div>
              <div v-if="numGrantees === 1" class="grantee-funding grantees-list-info">
                <v-icon
                  color="#aaa"
                  class="grantees-list-icon"
                  size="17">
                    {{ icons.money }}
                </v-icon>
                <div><span class="grantee-info-subheader">Funding: </span>{{ g.funding }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- MAP -->
        <div class="grid-map grid-section grid-second-row">
          <div id="state-and-offices">
            <CDProfilesPrintStateMap
              v-if="singleCDState === false"
              id="st-map"
              class="print-v" 
              :key="selectedCDFips"
              :selectedCDFips="selectedCDFips"
              :interactive="false"
              mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
              :colorPerServArea="colorPerServArea"
              :officesPerGrantee="officesPerGrantee"
            />
            <div id="offices-list">
              <div v-if="officesShown !== null && Object.keys(officesShown).length > 0">
                <v-icon color="#b31308" size="15">
                  {{ icons.main }}
                </v-icon> 
                Offices shown<span v-if="hasOfficeInDistrict === true && singleCDState === false"> (*in the district)</span>:
                <br v-if="hasOfficeInDistrict === true"/>
                <span v-for="rno in granteeDataPoints.map(x => x.rno)" :key="rno">
                  <span v-if="colorPerServArea.length > 1"><v-icon v-for="area in colorPerServArea.filter(x => x.rno === rno && Object.keys(officesShown).indexOf(rno) > -1)"
                    :key="area.color"
                    :color="area.color"
                    class="grantee-color"
                    size="14">
                      {{ icons.circle }}
                  </v-icon></span>
                  {{ officesShown[rno] }}
                </span>
              </div>
            </div>
          </div>
          <CDProfilesPrintMap
            id="cd-map"
            class="print-v" 
            :key="selectedCDFips"
            :selectedCDFips="selectedCDFips"
            :interactive="false"
            mapboxStyle="mapbox://styles/lsc-odga/clah3hb8i000914m3b08kcyjd"
            :colorPerServArea="colorPerServArea"
            :officesPerGrantee="officesPerGrantee"
            :stateName="profileData.state"
            @officesInView="parseOfficesList($event)"
          />
        </div>
      </div>
    </Block>
  </div>
</template>

<script>
import Block from '@/components/layout/Block'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import QrCode from '@/components/qr/QrCode'
import ProfileDataPoint from '@/components/st_cd_snapshots/ProfileDataPoint'
import CDProfilesPrintMap from '@/views/advocacy/congress/cd_profiles/CDProfilesPrintMap'
import CDProfilesPrintStateMap from '@/views/advocacy/congress/cd_profiles/CDProfilesPrintStateMap'
import { unique_colors } from '@/js/map_utils.js'
import { problemCodes, categoryRename, codesPerCategory } from '@/js/problem_codes.js'
import { mdiUndo, mdiHumanMaleFemale, mdiScaleBalance, mdiCircle, mdiAccountMultiple, mdiStarOutline, mdiGavel, mdiCurrencyUsd } from '@mdi/js';
import { formatThousandsNoDecimal, percentToOneInX, formatPercentNoDecimalIfExists } from "@/js/number_formatters.js";

export default {
  name: 'CDProfilesPrint',

  components: { Block, QrCode, LoadingSpinner, CDProfilesPrintMap, CDProfilesPrintStateMap, ProfileDataPoint },

  props: {
    profileData: { type: Object, required: true },
    selectedCDFips: { type: String, required: true },
    selectedCDLabel: { type: String, required: true },
    granteesByType: { type: Object, required: true },
    officesPerGrantee: { type: Object, required: true },
    granteesInState: { type: Array, required: true }
  },

  data () {
    return {
      icons: {
        rightArrow: mdiUndo,
        people: mdiHumanMaleFemale,
        scale: mdiScaleBalance,
        circle: mdiCircle,
        serves: mdiAccountMultiple,
        main: mdiStarOutline,
        gavel: mdiGavel,
        money: mdiCurrencyUsd
      },
      loading: true,
      qrUrl: '',
      garYear: null,
      fundingYear: null,
      housingPointsPrint: [],
      granteeDataPoints: [],
      colors: unique_colors,
      officesShown: null,
      hasOfficeInDistrict: false
    }
  },

  computed: {
    colorPerServArea () {
      let areas = []
      let c = this.colors
      for (let i = 0; i < this.granteesByType.bfGeneral.length; i++) {
        let a = this.granteesByType.bfGeneral[i]
        if (Object.keys(this.profileData.grantees).indexOf(a.recipient_id) > -1) { // Filter out any grantees that rounded to serving 0.0% of constituents
          areas.push({ area: a.serv_area, color: c[i], rno: a.recipient_id})
        }
      }
      return areas
    },
    singleGranteeState () {
      return this.granteesInState.filter(x => x.serv_area_type === 'Basic Field').length === 1
    },
    singleCDState () {
      return this.selectedCDFips.substring(2,4) === '00' || this.selectedCDFips.substring(2,4) === '98'
    },
    numGrantees () {
      return Object.keys(this.profileData.grantees).length
    }
  },

  created () {
    this.parseProfileData()
  },
  
  methods: {
    async parseProfileData () {
      this.loading = true
      this.loadQr()
      this.setHousingPoints(this.profileData)
      this.setGranteeDataPoints(this.profileData)
      this.loading = false
    },

    loadQr () {
      /**
       * Display a QR code for this URL (updating qrUrl will trigger the component)
       */
      this.$nextTick(function() {
        this.qrUrl = `https://www.lsc.gov/node/4035?embed=true&state=${this.selectedCDFips.substring(0,2)}&district=${this.selectedCDFips}&from=qr`
      })
      return
    },

    setHousingPoints (d) {
      /**
       * See similar functions in the state profiles
       */

      let printPoints = []
      const maxPrintPoints = 2

      // BOTH SLOTS DEPEND ON DATA AVAILABILITY AND THRESHOLDS
      // First priority is if eviction filing representation rates are available and the difference between them is at least 40 percentage points
      if ("eviction_representation" in d.housing && d.housing.eviction_representation !== null && "tenant" in d.housing.eviction_representation && "landlord" in d.housing.eviction_representation && (d.housing.eviction_representation.landlord - d.housing.eviction_representation.tenant > 0.4 )) {
        printPoints.push(
          { name: 'Eviction filing representation rates', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.tenant)}`, text: "of tenants have representation in court, compared to", comparisonDataPoint: `${formatPercentNoDecimalIfExists(d.housing.eviction_representation.landlord)}`, afterText: "of landlords.", inlinePoint: true}
        )
      }

      // Then, if the highest eviction filings rate amongst counties is greater than 10%
      if ("eviction_filings_rate" in d.housing && d.housing.eviction_filings_rate !== null && d.housing.eviction_filings_rate.rate > 0.1) {
        printPoints.push(
          { name: 'Filings rate', introText: `The rate of eviction filings is as high as`, dataPoint: `${percentToOneInX(d.housing.max_county_eviction_filings_rate.rate)}`, text: 'renter households.', sideBySide: true }
        )
      }

      // Percent rental cost burdened
      if (d.housing.pct_rent_burdened !== null) {
        printPoints.push(
          { name: 'Rental cost burdened', dataPoint: `${formatPercentNoDecimalIfExists(d.housing.pct_rent_burdened)}`, text: 'of renter households pay more than 30% of income on rent.', sideBySide: true }
        )
      }

      // Work hours to afford 1 bedroom at FMR -- if above 65 hours
      if (d.housing.nlihc !== null && "hrs_wk_for_1br_fmr" in d.housing.nlihc && d.housing.nlihc.hrs_wk_for_1br_fmr !== null && d.housing.nlihc.hrs_wk_for_1br_fmr > 65) {
        printPoints.push(
          { name: 'NLIHC hours of work', dataPoint: `${String(d.housing.nlihc.hrs_wk_for_1br_fmr)} hours/week`, text: `at minimum wage to afford a 1-bedroom unit at Fair Market Rent in ${d.state}`, sideBySide: false }
        )
      }

      // Units per ELI renter HH
      if (d.housing.nlihc !== null && "units_per_eli" in d.housing.nlihc && d.housing.nlihc.units_per_eli !== null && d.housing.nlihc.units_per_eli < 65) {
        printPoints.push(
          { name: 'NLIHC units per ELI', dataPoint: `Only ${String(d.housing.nlihc.units_per_eli)}`,text: "affordable units are available for every 100 extremely low-income renter households.", inlinePoint: true}
        )
      }

      this.housingPointsPrint = printPoints.slice(0, maxPrintPoints)
      return
    },

    setGranteeDataPoints(d) {
      let granteesList = []
      for (let rno of Object.keys(d.grantees)) {
        granteesList.push({ rno: rno, data: d.grantees[rno], cases: this.setCasesPoints(d.grantees[rno]), funding: this.setFundingPoints(d.grantees[rno]) })
      }
      this.granteeDataPoints = granteesList.sort((a,b) => b.data.percent_of_constituents - a.data.percent_of_constituents )
    },

    setCasesPoints (d) {
      let printPoints = ''
      let printPointsTally = 0

      let maxPrintPoints
      if (this.numGrantees === 1) {
        maxPrintPoints = 8
      } else if (this.numGrantees === 2) {
        maxPrintPoints = 5
      } else {
        maxPrintPoints = 1
      }

      // Convert categories and codes to new array of objects for sorting 
      const categories = this.keyValuesToSortedArrayOfObjects(d.closed_cases.categories)
      const codes = this.keyValuesToSortedArrayOfObjects(d.closed_cases.codes_over_10_pct)
    
      // Exclude the "other_" codes from each category
      const codesToExclude = ['09', '19', '29', '39', '49', '59', '69', '79', '89', '99']
      const miscCodes = ['90','91','92','93','94','95','96','97','98']

      // Treat problem codes under "Miscellaneous" as if they were problem categories
      for (let x of codes) {
        if (miscCodes.indexOf(x.key) > -1) {
          categories.push(x)
        }
      }

      categories.sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

      // Until we reach the max number of points for display,
      // add each problem category (and its sub-codes) in order for the list of data points
      for (const c of categories) {

        // Stop if have enough for what can fit in the display
        if (printPointsTally >= maxPrintPoints) { break }
        if (c.value <= 0.02) { continue }
        
        if (c.value > 0.02 && categoryRename[c.key] !== 'Miscellaneous' && categoryRename[c.key] !== 'Misc') {
          let isCodeWithinMiscCategory = miscCodes.indexOf(c.key) > -1
          let name = isCodeWithinMiscCategory === true ? problemCodes.filter(x => x.code === parseInt(c.key))[0].shortName : categoryRename[c.key]

          printPoints += `${name} ${formatPercentNoDecimalIfExists(c.value)}`
          printPointsTally += 1
          if (isCodeWithinMiscCategory === true && printPointsTally < maxPrintPoints) { printPoints += ', ' }
        }
        
        // Show any codes within this category if > 10% of all cases closed
        if (miscCodes.indexOf(c.key) > -1 || c.key === 'misc') { 
          continue 
        }

        const codesForThisCategory = codes.filter(x => codesPerCategory[c.key].indexOf(parseInt(x.key)) > -1 && codesToExclude.indexOf(x.key) === -1 && x.value > 0.1)

        if (codesForThisCategory.length > 0) {
          printPoints += ' '
          for (let i = 0; i < codesForThisCategory.length; i++) {
            if (printPointsTally >= maxPrintPoints) { continue }
            let co = codesForThisCategory[i]
            if (i === 0) { printPoints += '(' }
            printPoints += `${problemCodes.filter(x => x.code === parseInt(co.key))[0].shortName} ${formatPercentNoDecimalIfExists(co.value)}`
            if (i < codesForThisCategory.length - 1) { printPoints += ', '}
            if (i === codesForThisCategory.length - 1) { printPoints += ')' }
            printPointsTally += 1
          }
        }


        if (printPointsTally < maxPrintPoints) { printPoints += ', ' }
      }
      return printPoints.replace(/,+$|,\s+$/g, '') // remove commas or spaces at start or end of the string
    },

    sumObjValues (obj) {
      return Object.values(obj).reduce((a, b) => a + b)
    },

    setFundingPoints (d) {
      let fundingInfo = ''
      let bfTotal = this.formatThousandsNoDecimal(this.sumObjValues(d.funding.basic_field))
      let revenueTotal = this.formatThousandsNoDecimal(d.funding.total_revenue)

      fundingInfo += `$${revenueTotal} total revenue in ${this.profileData.gar_year}; granted $${bfTotal} of LSC Basic Field grants for ${d.funding.funding_year}`

      if (d.funding.special_grants.disaster + d.funding.special_grants.pbif + d.funding.special_grants.tig > 0) {
        fundingInfo += `; and since ${d.funding.special_grants.since_year} received `
        if (d.funding.special_grants.disaster > 0) {
          fundingInfo += `$${formatThousandsNoDecimal(d.funding.special_grants.disaster)} for disasters`
          if (d.funding.special_grants.pbif > 0 || d.funding.special_grants.tig > 0) {
            fundingInfo += ', '
          }
        }
        if (d.funding.special_grants.pbif > 0) {
          fundingInfo += `$${formatThousandsNoDecimal(d.funding.special_grants.pbif)} for pro bono innovation`
          if (d.funding.special_grants.tig > 0) {
            fundingInfo += ', '
          }
        }
        if (d.funding.special_grants.tig > 0) {
          fundingInfo += `$${formatThousandsNoDecimal(d.funding.special_grants.tig)} for tech innovation`
        }
      }
      return fundingInfo
    },

    keyValuesToSortedArrayOfObjects(obj) {
      /**
       * Convert an object like { key1: value1, key2: value2 } to an array of objects 
       * like [{ key: key1, value: value1 }, { key: key2, value: value2 }]
       * @param {object}  obj Object like { key1: value1, key2: value2 }
       * @return {array}  Returns array of objects
       */
      return Object.entries(obj).map(([key, value]) => ({key, value})).sort((a, b) => b.value - a.value)
    },

    formatPercentNoDecimalIfExists (val) { return formatPercentNoDecimalIfExists(val) },
    percentToOneInX (val) { return percentToOneInX(val) },
    formatThousandsNoDecimal (val) { return formatThousandsNoDecimal(val) },

    parseOfficesList(e) {
      let o = {}
      for (let g of Object.keys(e)) {
        let citiesUnique = []
        let citiesWithCount = []
        for (let i = 0; i < e[g].length; i++) {
          if (e[g][i].properties.inCD === "true") { this.hasOfficeInDistrict = true }
          let city = e[g][i].properties.city
          if (citiesUnique.indexOf(city) === -1) {
            citiesUnique.push(city)
            let countOfCity = e[g].map(x => x.properties.city).filter(x => x === city).length
            let inCDAsterisk = e[g].filter(x => x.properties.city === city && x.properties.inCD === 'true').length > 0 && this.singleCDState === false ? '*' : ''
            if (countOfCity === 1) {
              citiesWithCount.push(`${inCDAsterisk}${city}`)
            } else if (countOfCity > 1) {
              citiesWithCount.push(`${inCDAsterisk}${city} (x${countOfCity})`)
            }
          }
        }
        o[g] = citiesWithCount.sort().join(', ')
      }
      this.officesShown = o
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';
.print-instruction {
  max-width: 60rem;
  color: #5a5a5a;
  font-style: italic;
  text-align: center;
  margin: 0 auto 5rem auto;
}
#cd-summary-area-print {
  display: grid;
  grid-template-areas:
    'header header header header header header'
    'gap gap gap housing housing housing'
    'grantees grantees grantees map map map';
  gap: 0.5rem;
  font-size: 0.85rem;
  grid-auto-columns: 1fr;
  max-width: 43rem;
  min-width: 43rem;
  margin: 0 auto;

  .grid-header {
    grid-area: header;
    max-height: 5.5rem;
    min-height: 5.5rem;

    display: flex;
    justify-content: space-between;

    .grid-header-left {
      display: flex;
      flex-direction: column;

      .main-title {
        display: flex;
        flex-direction: column;
        justify-content: flex-start; 

        .title-1 {
          padding-top: 0.55rem;
          color: #404040;
          text-transform: uppercase;
        }
        .title-2 h2 {
          color: #828282;
          font-family: "Work Sans", sans-serif;
          margin-top: -0.3rem;
          font-size: 1.3rem;
          font-weight: 400;
        }
      }
    }

    .grid-header-right {
      display: flex;

      .grid-lsc-and-qr-info {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      #summary-logo-img {
        height: 55px;
        width: auto;
        display: block;
        margin-right: 1rem;
        padding: 0;
      }

      .qr-info {
        text-align: right;
        color: #5a5a5a;
        font-style: italic;
        margin-right: 0.45rem;
      }

      #qr-right-arrow {
        color: #5a5a5a;
        opacity: 0.5;
        transform: rotate(180deg);
      }
    }
  }
  .grid-gap {
    grid-area: gap;
    border-radius: 1rem;
    padding: 1rem 0;
  }
  .grid-housing {
    grid-area: housing;
    border-bottom: 1px dashed #ddd;
    margin-left: 0.5rem;
    padding: 1rem 0 1rem 1rem;
  }
  .grid-map {
    grid-area: map;
    display: flex;
    flex-direction: column;

    #state-and-offices {
      display: flex;
      flex-grow: 1;
      
      #st-map {
        width: 35%;
        min-width: 35%;
        max-width: 35%;
      }
      #offices-list {
        display: flex;
        align-self: center;
        font-size: 0.75rem;
        flex-grow: 1;
        padding-left: 0.25rem;
      }
    }
  }
  .grid-grantees {
    grid-area: grantees;
  }
  .grid-first-row {
    max-height: 26rem;
    min-height: 26rem;
  }
  .grid-second-row {
    max-height: 26rem;
    min-height: 26rem;
    overflow: hidden;

    &.custom-grid-section {
      padding-top: 1rem;
    }
  }
  .custom-grid-section {
    position: relative;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    
    &.shaded {
      box-shadow: rgb(17 17 26 / 5%) 2px 4px 13px;
      background-color: $extra-subtle-blue;
    }
    .section-title {
      color: $justice-gap-dark-blue;
      padding-bottom: 0.5rem;
      font-family: 'Work Sans', sans-serif;
      letter-spacing: 0.04rem;
      font-weight: 400;
      z-index: 1;
      position: relative;
      font-size: 0.95rem;
      letter-spacing: 0.05rem;
      text-align: center;

      &.grantee-count-1 {
        margin-top: 1.5rem;
      }
      &.grantee-count-2 {
        margin-top: 1rem;
      }

      &:not(:first-child) {
        margin-top: 1.5rem;
      }
      .section-title-emphasis {
        font-size: 1.25rem;
      }
      .section-subtitle {
        opacity: 0.7;
      }
    }
    #justice-gap-points {
      margin: 0.5rem 1rem 0 1rem;

      .justice-gap-row {
        display: flex;
        flex-direction: row;
        align-items: baseline;

        .justice-gap-data-point {
          width: 5.5rem;
          min-width: 5.5rem;
          max-width: 5.5rem;
          color: $header-red;
          font-size: 1.25rem;
          font-family: 'Work Sans', sans-serif;
          font-weight: 400;
          text-align: right;
          margin-right: 1rem;
        }
        .justice-gap-data-text {
          margin-bottom: 0.5rem;
          font-weight: normal;
        }
      }
    }
    #justice-gap-icon-design {
      display: flex;
      justify-content: space-between;
      margin: 0.25rem 1rem 1rem 1rem;
      opacity: 0.4;
      
      .icon-div {
        width: 2rem;
        margin-bottom: -0.125rem;
      }
      #dotted-path {
        flex-grow: 1;
        margin: 0 0.5rem;
        border-bottom: 4px dotted #5a5a5a;
      }
    }
    #served-in-district {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    #justice-gap-eligible {
      display: flex;
      flex-direction: column;
      margin-left: 1rem;
      margin-right: 0.5rem;
      
      .jg-eligible-row {
        min-width: 100%;
        display: flex;
        flex-direction: row;
        align-items: baseline;
        margin-bottom: 0.5rem;
        justify-content: space-between;
      }

      #eligible-header {
        color: $justice-gap-dark-blue;
        font-size: 0.95rem;
      }
      >div {
        margin-right: 0.75rem;
        margin-bottom: 0.5rem;
        max-width: 45%;
        min-width: 45%;
      }
    }
    #served-in-district {
      justify-content: space-between;
      
      >div {
        margin-bottom: 0.25rem;
      }
    }
    #housing-data {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      justify-content: space-around;
    }
    #grantees-list {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-around;

      .grantees-list-g {
        padding: 0.5rem 0;
      }
      .grantee-name-row {
        display: flex;
      }
      .grantee-color {
        margin-right: 0.5rem;
      }
      .grantee-name {
        font-size: 0.9rem;
        font-weight: 400;
        color: #404040;
        margin-bottom: 0.5rem;
      }
      .grantees-list-info {
        display: flex;
        margin: 0.25rem 0 0.5rem 0.325rem;
      }
      .grantee-count-1 .grantees-list-info {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
      .grantee-count-1 .grantees-list-info {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
      .grantee-info-subheader {
        font-weight: 400;
      }
      .grantees-list-icon {
        margin-right: 0.45rem;
      }
    }
  }
}

</style>